/**
 * ATTRACT LOOP (B6): plays the §G script until its signal aborts. DOM-free; everything injectable.
 *
 * It drives the map the way a visitor would, through the store (the bridge turns store changes into
 * choreographer calls, spec B4 handoff): a bloom scene is setCategory(c) + bloom(c), "select nearest"
 * is select(id, "attract"), a scene's exit is select(null) then bloom(null). Only scene 0 calls the
 * choreographer directly (home(): camera home, the site's tree rings and contours).
 *
 * A scene's dwell starts when it has arrived (home settled, bloom:done, route:drawn for the selection),
 * each wait capped so a missing event can never stall the loop. Between scenes: the caption leaves,
 * the exit plays (until every drawn route reported route:cleared, capped), then a 600 ms pause.
 *
 * Boundaries (before each scene): resetIfDue() (the kiosk controller resets the app at 180 s and the
 * loop restarts at scene 0, loop 0); at scene 0 reloadIfDue() (every 6 h) and the loop's mood
 * (always day: the time-of-day control is hidden).
 */
import type { BusEvents } from "@/contracts/bus";
import type { Choreographer } from "@/contracts/map";
import type { AppActions, AppState } from "@/contracts/store";
import { isAbort, wait } from "@/lib/ticker";
import { ATTRACT, ATTRACT_SCENES, captionFor, categoryStats, moodForLoop, type Caption, type SceneDef } from "./script";

export type BusOn = <K extends keyof BusEvents>(type: K, fn: (e: BusEvents[K]) => void) => () => void;

export interface AttractDeps {
  getState: () => Pick<AppState, "data" | "selectedId" | "bloomCategory" | "query">;
  actions: Pick<AppActions, "select" | "bloom" | "setCategory" | "setMood" | "setQuery">;
  choreo: () => Choreographer | null;
  on: BusOn;
  /** The caption on screen (null between scenes). */
  caption: (c: Caption | null, scene: number, loop: number) => void;
  /** At every scene boundary. true: the app was reset; the loop restarts at scene 0, loop 0. */
  resetIfDue: () => boolean;
  /** At every scene-0 boundary. true: the page is reloading; the loop stops. */
  reloadIfDue: () => boolean;
  /** Test seam; default lib/ticker wait (rejects with AbortError on abort). */
  wait?: (ms: number, signal: AbortSignal) => Promise<void>;
}

/** Resolves when `p` settles or after `ms`, whichever is first; rejects on abort. */
function within(p: Promise<unknown>, ms: number, signal: AbortSignal, sleep: (ms: number, s: AbortSignal) => Promise<void>): Promise<void> {
  const inner = new AbortController();
  const onAbort = () => inner.abort();
  signal.addEventListener("abort", onAbort, { once: true });
  return Promise.race([
    p.then(
      () => undefined,
      () => undefined,
    ),
    sleep(ms, inner.signal).catch((e: unknown) => {
      if (!signal.aborted) return undefined; // our own cleanup
      throw e;
    }),
  ]).finally(() => {
    signal.removeEventListener("abort", onAbort);
    inner.abort();
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  });
}

/** Waits for the first matching bus event (true) or the cap (false); rejects on abort. Subscribes at once. */
export function waitForEvent<K extends keyof BusEvents>(
  on: BusOn,
  type: K,
  match: (e: BusEvents[K]) => boolean,
  capMs: number,
  signal: AbortSignal,
  sleep: (ms: number, s: AbortSignal) => Promise<void> = wait,
): Promise<boolean> {
  let hit = false;
  let resolveHit!: () => void;
  const got = new Promise<void>((r) => (resolveHit = r));
  const off = on(type, (e) => {
    if (!hit && match(e)) {
      hit = true;
      resolveHit();
    }
  });
  return within(got, capMs, signal, sleep).then(
    () => {
      off();
      return hit;
    },
    (err: unknown) => {
      off();
      throw err;
    },
  );
}

/** Plays one scene up to the end of its dwell. Returns the route ids it drew (for the exit). */
async function playScene(d: AttractDeps, scene: SceneDef, loop: number, signal: AbortSignal): Promise<Set<string>> {
  const sleep = d.wait ?? wait;
  const data = d.getState().data;
  const drawn = new Set<string>();
  d.caption(captionFor(scene, data, loop), scene.index, loop);

  if (scene.kind === "home") {
    d.actions.setCategory(null);
    const c = d.choreo();
    if (c) await within(c.home(), ATTRACT.homeCapMs, signal, sleep);
    await sleep(scene.dwellMs, signal);
    return drawn;
  }

  // Future infrastructure draws no routes (its corridors light instead), so it has nothing to retract.
  const drawsRoutes = data?.categories.find((c) => c.id === scene.category)?.kind !== "future";
  const offArrive = d.on("bloom:arrive", (e) => {
    if (drawsRoutes && e.categoryId === scene.category) drawn.add(e.placeId);
  });
  try {
    const arrived = waitForEvent(d.on, "bloom:done", (e) => e.categoryId === scene.category, ATTRACT.bloomCapMs, signal, sleep);
    d.actions.setCategory(scene.category);
    d.actions.bloom(scene.category);
    await arrived;
    await sleep(scene.dwellMs, signal);

    if (scene.selectDwellMs != null) {
      const id = categoryStats(data, scene.category).nearest?.id;
      if (id) {
        const landed = waitForEvent(d.on, "route:drawn", (e) => e.placeId === id, ATTRACT.selectCapMs, signal, sleep);
        drawn.add(id);
        d.actions.select(id, "attract");
        await landed;
        await sleep(scene.selectDwellMs, signal);
      }
    }
  } finally {
    offArrive();
  }
  return drawn;
}

/**
 * The previous scene leaves: selection first (the camera eases back to the bloom overview), then the
 * bloom retracts farthest first. Resolves when every drawn route reported route:cleared (capped).
 */
async function clearScene(d: AttractDeps, drawn: Set<string>, signal: AbortSignal): Promise<void> {
  const sleep = d.wait ?? wait;
  const s = d.getState();
  if (!s.selectedId && !s.bloomCategory) return;
  const pending = new Set(drawn);
  let resolveAll!: () => void;
  const all = new Promise<void>((r) => (resolveAll = r));
  const off = d.on("route:cleared", (e) => {
    pending.delete(e.placeId);
    if (!pending.size) resolveAll();
  });
  try {
    if (s.selectedId) d.actions.select(null);
    if (s.bloomCategory) d.actions.bloom(null);
    if (pending.size) await within(all, ATTRACT.exitCapMs, signal, sleep);
  } finally {
    off();
  }
}

/** The §G loop. Resolves when the signal aborts (wake, disable) or the page reloads. */
export async function runAttract(d: AttractDeps, signal: AbortSignal): Promise<void> {
  const sleep = d.wait ?? wait;
  let loop = 0;
  let i = 0;
  try {
    // Soft clear under the overlay's 640 drift cross-fade: deselect, clear the bloom and the search.
    const s = d.getState();
    if (s.query) d.actions.setQuery("");
    if (s.selectedId) d.actions.select(null);
    if (s.bloomCategory) d.actions.bloom(null);
    d.actions.setCategory(null);
    await sleep(ATTRACT.softClearMs, signal);

    for (;;) {
      if (signal.aborted) return;
      if (d.resetIfDue()) {
        loop = 0;
        i = 0;
      }
      if (i === 0) {
        if (d.reloadIfDue()) return;
        d.actions.setMood(moodForLoop(loop));
      }
      const scene = ATTRACT_SCENES[i];
      const drawn = await playScene(d, scene, loop, signal);
      d.caption(null, scene.index, loop);
      await clearScene(d, drawn, signal);
      await sleep(ATTRACT.pauseMs, signal);
      i += 1;
      if (i >= ATTRACT_SCENES.length) {
        i = 0;
        loop += 1;
      }
    }
  } catch (err) {
    if (!isAbort(err) && !signal.aborted) throw err;
  }
}
